import React, { Fragment, useCallback, useContext, useMemo, useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { token } from '@atlaskit/tokens';

import { UniversalCreateButton } from '@atlassian/universal-create';

import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { fg } from '@confluence/feature-gating';
import { START, END } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { AppNavigationContext } from '@confluence/app-navigation-context';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import { useIsSmartFoldersEnabled } from '@confluence/smart-folder/entry-points/useIsSmartFoldersEnabled.ts';
import { useIsInSmartFolderTree } from '@confluence/smart-folder/entry-points/useIsInSmartFolderTree.ts';

import { useUniversalCreateState, useUniversalCreateActions } from './useUniversalCreate.ts';
import { ANALYTICS_EVENT_SOURCE, useMenuItems } from './useMenuItems/index.tsx';
import { useAISmartCreateItem } from './useMenuItems/useAISmartCreateItem.tsx';
import { useSmartCreatePreload } from './useSmartCreatePreload.ts';
import { useCwRSpotlightOrchestrator } from './cwrExperiment/spotlight/useCwRSpotlightOrchestrator.tsx';
import { useCwrSplitButtonExperiment } from './cwrExperiment/useCwrSplitButtonExperiment.ts';

const messages = defineMessages({
	create: {
		id: 'create-blank-fabric-page.universal-create.create.text',
		defaultMessage: 'Create',
		description: 'The Create text that appears in the top nav',
	},
	managedFolderCreateErrorTitle: {
		id: 'create-blank-fabric-page.universal-create.managed-folder-create-error.title',
		defaultMessage: 'We’re unable to create here',
		description:
			'Title for an error flag shown when someone tries to create content from the global Create button while viewing a managed folder or content inside a managed folder.',
	},
	managedFolderCreateErrorDescription: {
		id: 'create-blank-fabric-page.universal-create.managed-folder-create-error.description',
		defaultMessage:
			'This folder is synced from another app. Choose another location to create content.',
		description:
			'Description for an error flag explaining that global content creation is blocked because the current folder tree is synced from another app and read-only.',
	},
});

export const getCreateButtonTheme =
	(createButtonTheme: any) =>
	(
		current: any,
		props: any,
	): {
		buttonStyles: any;
		spinnerStyles: any;
	} => {
		const { buttonStyles, spinnerStyles } = current(props);
		return {
			buttonStyles: {
				...buttonStyles,
				...createButtonTheme.default,
				margin: 0,
				fontWeight: token('font.weight.medium'),
				':hover': createButtonTheme.hover,
				':focus': createButtonTheme.focus,
				// :active doesn't work in FF, because we do a
				// e.preventDefault() on mouse down in Button.
				// '&&' is required to add more CSS specificity
				// && it not a valid CSSObject property
				// @ts-ignore
				'&&': {
					...(props.state === 'active' && createButtonTheme.active),
				},
			},
			spinnerStyles,
		};
	};

const Create = ({ flags }) => {
	const { isOpen } = useUniversalCreateState();
	const { setIsOpen, setOpenFrom } = useUniversalCreateActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { match } = useContext(RoutesContext);
	const { theme } = useContext(AppNavigationContext);
	const isOnEditRoute = useIsEditorPage();
	const { formatMessage } = useIntl();
	const { isRovoLLMEnabled, accessStatus, isLoggedIn } = useSessionData();
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();
	const { isSmartFoldersEnabled, loading: isSmartFoldersEnabledLoading } =
		useIsSmartFoldersEnabled(spaceKey);
	const { isInSmartFolderTree, loading: isSmartFolderTreeLoading } = useIsInSmartFolderTree(
		isSmartFoldersEnabled ? contentId : undefined,
	);
	// While either query is still resolving (and we have a contentId to check),
	// we don't yet know whether we're in a managed folder tree. Treat this as
	// a pending state and block the Create menu until the checks settle.
	const isSmartFolderCheckPending =
		!!contentId && (isSmartFoldersEnabledLoading || isSmartFolderTreeLoading);
	const routeDataRef = useRouteDataRef();
	const isLivePage = useIsCurrentPageLive();
	const { isEnabled: isCwrSplitButtonEnabled } = useCwrSplitButtonExperiment();
	const isServerPlaceholder = useRenderServerPlaceholder();
	const menuItems = useMenuItems(flags, () => setIsOpen(false), undefined, isOpen);
	const buttonTheme = theme?.mode?.create ? getCreateButtonTheme(theme.mode.create) : undefined;
	const isAnonymousUser = accessStatus === AccessStatus.ANONYMOUS_ACCESS || !isLoggedIn;
	const isAIFCCreateEnabled = isRovoLLMEnabled && !isAnonymousUser && fg('aifc_create_enabled');
	const experimentSpotlightEnabled =
		!isServerPlaceholder &&
		isAIFCCreateEnabled &&
		isCwrSplitButtonEnabled &&
		fg('navx_uc_cwr_experiment_spotlight_enabled');

	const showManagedFolderCreateError = useCallback(() => {
		void flags.showErrorFlag({
			id: 'global-create-managed-folder-tree-error',
			title: <FormattedMessage {...messages.managedFolderCreateErrorTitle} />,
			description: <FormattedMessage {...messages.managedFolderCreateErrorDescription} />,
		});
	}, [flags]);

	const onCreateClick = useCallback(() => {
		const willOpen = !isOpen;
		const isBlockedByManagedFolderTree =
			willOpen && (isInSmartFolderTree || isSmartFolderCheckPending);

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'navigationItem',
				actionSubjectId: 'globalCreateContentPopover',
				source: 'globalNavigation',
				attributes: {
					selectedItemPageContext: match?.name,
					navigationLayer: 'global',
					navigationContainer: 'top',
					navVersion: '4',
					navdexPointType: START,
					isOnEditRoute,
					...(isBlockedByManagedFolderTree && { blockedReason: 'managedFolderTree' }),
				},
			},
		}).fire();

		if (isBlockedByManagedFolderTree) {
			showManagedFolderCreateError();
			return;
		}

		void setIsOpen(willOpen);
	}, [
		createAnalyticsEvent,
		isInSmartFolderTree,
		isSmartFolderCheckPending,
		isOpen,
		isOnEditRoute,
		match,
		setIsOpen,
		showManagedFolderCreateError,
	]);

	// Duplicated from useMenuItems hook
	const handleAnalyticsAndResetStickySearch = useCallback(
		(actionSubjectId: string, attributes?: Record<string, string>) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId,
					source: ANALYTICS_EVENT_SOURCE,
					attributes: {
						navdexPointType: END,
						isOnEditRoute,
						currentRouteName: routeDataRef?.current?.routeName,
						currentRouteIsLive: isLivePage,
						...attributes,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, isOnEditRoute, routeDataRef, isLivePage],
	);

	const onSmartCreateClick = useCallback(
		(actionSubjectId: string, attributes?: Record<string, string>) => {
			handleAnalyticsAndResetStickySearch(actionSubjectId, attributes);
			void setIsOpen(false);
		},
		[handleAnalyticsAndResetStickySearch, setIsOpen],
	);

	const smartCreateItem = useAISmartCreateItem({
		source: 'globalCreateDropdown-smartCreate',
		spaceKey,
		onClick: onSmartCreateClick,
		isDisabled: !isAIFCCreateEnabled,
	});

	const { preload: preloadSmartCreate } = useSmartCreatePreload({
		isDisabled: !isAIFCCreateEnabled,
		includePromptSuggestions: false,
	});
	const hasPreloadedSmartCreateRef = useRef(false);
	const onTriggerMouseEnter = useCallback(() => {
		if (hasPreloadedSmartCreateRef.current || !isAIFCCreateEnabled || !isCwrSplitButtonEnabled) {
			return;
		}
		hasPreloadedSmartCreateRef.current = true;
		preloadSmartCreate();
	}, [preloadSmartCreate, isAIFCCreateEnabled, isCwrSplitButtonEnabled]);

	// Reset the openFrom state when the create menu is closed.
	const onClose = () => {
		void setIsOpen(false);
		void setOpenFrom('');
	};

	const crossAppCreationConfig = useMemo(() => ({ enabled: false }) as const, []);

	const originalPrimaryActionItem =
		isAIFCCreateEnabled && isCwrSplitButtonEnabled ? smartCreateItem : undefined;
	const primaryActionItem = useMemo(() => {
		if (!originalPrimaryActionItem) {
			return undefined;
		}

		return {
			...originalPrimaryActionItem,
			onClick: (event) => {
				if (isInSmartFolderTree || isSmartFolderCheckPending) {
					event?.preventDefault?.();
					handleAnalyticsAndResetStickySearch('createAISmartCreatePopoverItem', {
						blockedReason: 'managedFolderTree',
					});
					void setIsOpen(false);
					showManagedFolderCreateError();
					return;
				}

				originalPrimaryActionItem.onClick?.(event);
			},
		};
	}, [
		handleAnalyticsAndResetStickySearch,
		isInSmartFolderTree,
		isSmartFolderCheckPending,
		originalPrimaryActionItem,
		setIsOpen,
		showManagedFolderCreateError,
	]);

	const {
		onClick: createOnClick,
		onCloseMenu: createOnCloseMenu,
		primaryActionItem: createPrimaryActionItem,
		spotlight,
	} = useCwRSpotlightOrchestrator({
		enabled: experimentSpotlightEnabled,
		isMenuOpen: isOpen,
		onCloseMenu: onClose,
		onClick: onCreateClick,
		primaryActionItem,
	});

	return (
		<Fragment>
			<UniversalCreateButton
				sections={menuItems}
				crossAppCreationConfig={crossAppCreationConfig}
				onMouseEnter={onTriggerMouseEnter}
				triggerButtonSpotlightTarget="top-nav-create"
				enableTriggerPulse={false}
				isOpen={isOpen}
				onClose={createOnCloseMenu}
				onClick={createOnClick ?? onCreateClick}
				product="confluence"
				buttonTheme={buttonTheme}
				testId="create-content-popover-button"
				shouldRenderToParent
				{...(createPrimaryActionItem ? { primaryActionItem: createPrimaryActionItem } : {})}
			>
				{formatMessage(messages.create)}
			</UniversalCreateButton>
			{spotlight}
		</Fragment>
	);
};

const CreateWithFlags = withFlags(Create);

export function UniversalCreate(): React.JSX.Element {
	return (
		<TransparentErrorBoundary attribution={Attribution.NAVX}>
			<CreateWithFlags />
		</TransparentErrorBoundary>
	);
}
